// AITroubleAnalysisModal.tsx (修改后版本)
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Avatar, message } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { getBaseRequestUrl, getToken } from "@/utils/constants";
import ReactMarkdown from 'react-markdown';

const UserType = {
    user: 'user',
    bot: 'bot',
};

interface MessageType {
    type: string;
    data: string;
    id?: string;
    loading?: boolean;
    conversation_id?: string;
    answerJson?: any;
}

interface AITroubleAnalysisModalProps {
    visible: boolean;
    onCancel: () => void;
    dataVal: any;
}

const AITroubleAnalysisModal: React.FC<AITroubleAnalysisModalProps> = ({
    visible,
    onCancel,
    dataVal
}) => {
    const [messages, setMessages] = useState<MessageType[]>([]);
    const [newMessage, setNewMessage] = useState<string>('');
    const [hasAutoSent, setHasAutoSent] = useState<boolean>(false); // 新增：标记是否已自动发送
    const messagesRef = useRef<MessageType[]>([]);
    const chatWindowRef = useRef<HTMLDivElement>(null);

    const initialMessages = [
        {
            type: UserType.bot,
            data: '您好，我是维修助手AI，可以帮助您分析故障原因和维修方案。',
        },
    ];

    useEffect(() => {
        if (visible) {
            // 重置状态
            setMessages(initialMessages);
            setHasAutoSent(false);
            messagesRef.current = initialMessages;

            const autoMessage = buildAutoMessage();
            if (dataVal.faultDesc && dataVal.faultDesc.trim()) {
                setTimeout(() => {
                    // 构建自动发送的消息内容

                    setTimeout(() => {
                        sendMessage(autoMessage);
                        setHasAutoSent(true);
                    }, 100);
                }, 500);
            }
            else {
                setNewMessage(autoMessage);
            }
        } else {
            // 模态框关闭时重置状态
            setHasAutoSent(false);
            setNewMessage('');
        }
    }, [visible, dataVal]);

    // 构建自动发送的消息内容
    const buildAutoMessage = (): string => {
        let message = '';

        if (dataVal.faultDesc && dataVal.faultDesc.trim()) {
            message += `请分析以下故障描述：${dataVal.faultDesc}。`;
        }

        if (dataVal.brandName) {
            message += `车辆品牌：${dataVal.brandName}。`;
        }

        if (dataVal.vehicleTypeName) {
            message += `车辆类型：${dataVal.vehicleTypeName}。`;
        }
        if (!dataVal.faultDesc) {
            message += `请分析以下故障描述：`;
        }
        return message;
    };

    const scrollToBottom = () => {
        if (chatWindowRef.current) {
            chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
        }
    };

    useEffect(() => {
        scrollToBottom();
        messagesRef.current = messages;
    }, [messages]);

    const sendMessage = (customMessage?: string) => {
        if (messagesRef.current.findIndex((item) => item.loading) > -1) {
            return message.warning('AI正在思考中，请稍后再试！');
        }

        const query = customMessage ? customMessage.trim() : newMessage.trim();
        if (query) {
            const id = `${new Date().getTime()}${Math.floor(Math.random() * 10000000)}`;
            const newMessages = [
                ...messagesRef.current,
                { type: UserType.user, data: query, id },
                { type: UserType.bot, data: '', id, loading: true }
            ];
            setMessages(newMessages);
            fetchAIResponse(query, id);
            // 只有在不是自动发送的情况下才清空输入框
            if (!customMessage) {
                setNewMessage('');
            }
        }
    };

    const fetchAIResponse = async (query: string, id: string) => {
        let messageStr = '';
        let jsonStr = '';
        let isHind = false;

        try {
            await fetchEventSource(`${getBaseRequestUrl()}/yh-ai-server/chat/backend/chat-messages`, {
                method: 'POST',
                headers: {
                    Authorization: getToken(),
                    'auth-provider': 'zzw-portal',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    inputs: {},
                    query,
                    response_mode: 'streaming',
                    conversation_id: getConversationId(),
                    app_code: 'qy-ai-trainer',
                    files: []
                }),
                openWhenHidden: true,
                onopen: async (response: any) => {
                    if (response.status !== 200) {
                        messageStr = `请求失败，状态码：${response.status}`;
                    }
                    return Promise.resolve();
                },
                onmessage: (event: any) => {
                    if (event && event.data) {
                        const data = JSON.parse(event.data);
                        if (data.event === 'message') {
                            if (data.answer.includes('```')) {
                                isHind = !isHind;
                                const arr = data.answer.split('```');
                                messageStr += arr[0] || '';
                                addMessages({ data: messageStr, conversation_id: data.conversation_id, loading: true }, id);
                                jsonStr += arr[1] || '';
                                return;
                            }
                            if (!isHind) {
                                messageStr += data.answer;
                                addMessages({ data: messageStr, conversation_id: data.conversation_id, loading: true }, id);
                            } else {
                                jsonStr += data.answer;
                            }
                        }
                    }
                },
                onerror: (error: any) => {
                    console.error('AI请求错误:', error);
                    message.error('AI服务暂时不可用，请稍后重试');
                    addMessages({ data: '抱歉，AI服务暂时不可用，请稍后重试。', loading: false }, id);
                },
                onclose: () => {
                    let json = null;
                    try {
                        if (jsonStr) {
                            json = JSON.parse(jsonStr.replace('json', ''));
                        }
                    } catch (err) {
                        console.error('JSON解析失败:', err);
                    }
                    addMessages({ data: messageStr, answerJson: json, loading: false }, id);
                },
            });
        } catch (error) {
            console.error('AI请求异常:', error);
            message.error('网络请求失败，请检查网络连接');
            addMessages({ data: '网络请求失败，请检查网络连接后重试。', loading: false }, id);
        }
    };

    const addMessages = (data: any, id: string) => {
        const updatedData = { ...data, type: UserType.bot, id };
        const index = messagesRef.current.findIndex((item) => item.id === id && item.type === UserType.bot);

        if (index !== -1) {
            messagesRef.current[index] = { ...messagesRef.current[index], ...updatedData };
            setMessages([...messagesRef.current]);
        }
    };

    const getConversationId = () => {
        for (let i = messagesRef.current.length - 1; i >= 0; i--) {
            const item = messagesRef.current[i];
            if (item.type === UserType.bot && item.conversation_id) {
                return item.conversation_id;
            }
        }
        return '';
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            sendMessage();
        }
    };

    // 获取输入框的placeholder文本
    const getInputPlaceholder = (): string => {
        if (!hasAutoSent && dataVal.faultDesc && dataVal.faultDesc.trim()) {
            return buildAutoMessage();
        }
        return "请输入您的问题...";
    };

    return (
        <Modal
            title="AI故障分析助手"
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={800}
            maskClosable={false}
            style={{ top: '15%' }}
            bodyStyle={{ padding: '16px' }}
        >
            <ChatBox>
                <div className="App">
                    <div ref={chatWindowRef} className="chat-window">
                        {messages.map((msg, index) => (
                            <div key={msg.id || index} className="message-box">
                                <div className="avatar-box">
                                    {msg.type === UserType.user && <Avatar size={24} icon={<UserOutlined />} />}
                                    {msg.type === UserType.bot && (
                                        <img
                                            src="https://file-server.yunhaochefu.com/image/6cca15048a86475949dde44f5428e977"
                                            alt="AI助手"
                                        />
                                    )}
                                </div>
                                <div className="message-info">
                                    {msg.type === UserType.user && (
                                        <ReactMarkdown>{msg.data}</ReactMarkdown>
                                    )}
                                    {msg.type === UserType.bot && (
                                        <div className="bot-info">
                                            <ReactMarkdown>{msg.data}</ReactMarkdown>
                                            {msg.loading && <span className="loading-icon"></span>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="input-container">
                        <input
                            type="text"
                            placeholder={getInputPlaceholder()}
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                        />
                        <button onClick={() => sendMessage()}>发送</button>
                    </div>
                </div>
            </ChatBox>
        </Modal>
    );
};

// ChatBox 样式保持不变...
const ChatBox = styled.div`
  width: 100%;
  height: 500px;
  .App {
    width: 100%;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
    align-items: center;
    background: linear-gradient(180deg, #f0edf7 0.03%, #ebeaf5 32.19%, #e8e8f3 68.86%, #e4eaf7 99.12%);
    padding: 10px;
    box-sizing: border-box;
    border-radius: 10px;
  }

  .chat-window {
    width: 100%;
    flex: 1;
    border: 1px solid #dedede;
    border-radius: 10px;
    background: linear-gradient(180deg, #f5f4f6, #e6ebf7);
    overflow-y: auto;
    padding: 10px;
    box-sizing: border-box;
    margin-bottom: 10px;
  }

  .message-box {
    width: 100%;
    overflow: hidden;
    margin-bottom: 10px;
    display: flex;
    align-items: start;
  }

  .avatar-box {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: linear-gradient(90deg, #8c8ef7, #2e6ee7);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 5px;
    img {
      width: 70%;
      height: 70%;
    }
  }

  .message-info {
    flex: 1;
    margin-left: 10px;
    font-size: 14px;
  }

  .user-info {
    padding: 5px 10px;
    border-radius: 5px;
    background-color: #e7f8ff;
    display: inline-block;
  }

  .bot-info {
    padding: 5px 10px;
    border-radius: 5px;
    background-color: #fff;
    display: inline-block;
  }

  .loading-icon {
    display: inline-block;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid rgba(0, 0, 0, 0.1);
    border-top-color: #333;
    animation: spin 1s linear infinite;
    margin-left: 8px;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .input-container {
    display: flex;
    width: 100%;
  }

  .input-container input {
    flex: 1;
    padding: 10px;
    border: 1px solid #dedede;
    border-radius: 10px;
    box-sizing: border-box;
    height: 40px;
  }

  .input-container button {
    padding: 5px 15px;
    margin-left: 10px;
    border: none;
    border-radius: 10px;
    background: linear-gradient(90deg, #2e6ee7, #8c8ef7);
    color: #fff;
    cursor: pointer;
    height: 40px;
  }

  .input-container button:hover {
    background: linear-gradient(90deg, #255fd4, #7a7fd4);
  }

  pre, code {
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word'
  }

  p {
    margin-bottom: 0 !important;
  }
`;

export default AITroubleAnalysisModal;